import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, TextField } from "@mui/material";
import SubmitButton from "components/Buttons/SubmitButton";
import { useFormik } from "formik";
import { useState, useEffect } from "react";
import { Button, Col, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { splitBags } from "slices/Warehouse/ItemInward2/GRN/thunk";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";

interface RowData {
  id: string;
  quantity?: number;
}


interface CreateChildBagModalProps {
  show: boolean;
  handleClose: () => void;
  selectedItem: any;
  inventoryId?: string;
  onSuccess?: () => void;
}

const CreateChildBagModal = ({ 
  show, 
  handleClose, 
  selectedItem, 
  inventoryId,
  onSuccess 
}: CreateChildBagModalProps) => {
  const [rows, setRows] = useState<RowData[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingRows, setDeletingRows] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [availableQuantity, setAvailableQuantity] = useState<number>(0);
  const [totalQuantityUsed, setTotalQuantityUsed] = useState<number>(0);

  const dispatch = useDispatch<any>();

  // Update available quantity when selected item changes
  useEffect(() => {
    if (selectedItem) {
      const available = Number(selectedItem.available_quantity) || 0;
      setAvailableQuantity(available);
      // Calculate total quantity used from rows
      const total = rows.reduce((sum, row) => sum + (row.quantity || 0), 0);
      setTotalQuantityUsed(total);
    } else {
      setAvailableQuantity(0);
      setTotalQuantityUsed(0);
    }
  }, [selectedItem, rows]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setRows([]);
      setTotalQuantityUsed(0);
    }
  }, [show]);

  const validationSchema = Yup.object({
    quantity: Yup.number()
      .typeError("Quantity must be a number")
      .positive("Quantity must be positive")
      .required("Quantity is required")
      .test(
        "max-available",
        `Total quantity cannot exceed available quantity (${availableQuantity})`,
        function (value) {
          if (!value) return true;
          const currentTotal = rows.reduce((sum, row) => sum + (row.quantity || 0), 0);
          return currentTotal + value <= availableQuantity;
        }
      ),
  });

  const formik = useFormik({
    initialValues: { quantity: "" },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { resetForm }) => {
      const newQuantity = Number(values.quantity);
      const currentTotal = rows.reduce((sum, row) => sum + (row.quantity || 0), 0);
      
      if (currentTotal + newQuantity > availableQuantity) {
        formik.setFieldError("quantity", `Total quantity cannot exceed ${availableQuantity}`);
        return;
      }

      setIsAdding(true);
      const newRow: RowData = {
        id: Date.now().toString(),
        quantity: newQuantity,
      };
      setTimeout(() => {
        setRows([...rows, newRow]);
        resetForm();
        setIsAdding(false);
      }, 300);
    },
  });

  const handleDelete = (id: string) => {
    setDeletingRows((prev) => [...prev, id]);
    setTimeout(() => {
      setRows(rows.filter((row) => row.id !== id));
      setDeletingRows((prev) => prev.filter((rowId) => rowId !== id));
    }, 300);
  };

  const handleSubmit = async () => {
    if (!selectedItem || !selectedItem.inventory_item_id) {
      toast.error("No item selected");
      return;
    }

    if (rows.length === 0) {
      toast.warning("Please add at least one child bag");
      return;
    }

    // Validate total quantity doesn't exceed available
    const totalQuantity = rows.reduce((sum, row) => sum + (row.quantity || 0), 0);
    if (totalQuantity > availableQuantity) {
      toast.error(`Total quantity (${totalQuantity}) cannot exceed available quantity (${availableQuantity})`);
      return;
    }

    setSubmitting(true);
    try {
      const childBags = rows.map((row) => ({
        quantity: row.quantity || 0,
      }));

      await dispatch(
        splitBags({
          inventory_item_id: selectedItem.inventory_item_id,
          child_bags: childBags,
        })
      ).unwrap();

      toast.success("Child bags created successfully");
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error splitting bags:", error);
      const errorMessage = error?.error || error?.response?.data?.error ||
        error?.message || error?.response?.data?.message || "Failed to create child bags";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // console.log(selectedItem);

  return (
    <Modal show={show} onHide={handleClose} centered size="xl">
      <Modal.Header className="border-0">
        <Modal.Title className="fw-bold">Create Child Bag</Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleClose}
        />
      </Modal.Header>
      <hr />
      <Modal.Body>
        {/* First table (item details) */}
        <div style={{ overflowX: "auto" }}>
          <Col lg={3} className="me-auto">
            <h6 className="card-title">Item Details</h6>
          </Col>
          <table className="table">
            <thead className="table-light">
              <tr>
                <th style={{ minWidth: "80px", maxWidth: "100px" }}>Sr No.</th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>
                  Item Name
                </th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>
                  Item Code
                </th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>
                  Category
                </th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>
                  Sub Category
                </th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>Unit</th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>
                  Received Quantity
                </th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>
                  Reserved Quantity
                </th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>
                  Available Quantity
                </th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>
                  Bag No.
                </th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>Rate</th>
                <th style={{ minWidth: "120px", maxWidth: "160px" }}>Amount</th>
                <th style={{ minWidth: "120px", maxWidth: "200px" }}>Remark</th>
              </tr>
            </thead>
            <tbody>
              {selectedItem ? (
                <tr>
                  <td>1</td>
                  <td>{selectedItem.master_item?.item_name || "-"}</td>
                  <td>{selectedItem.master_item?.item_code || "-"}</td>
                  <td>{selectedItem.master_item?.category?.category_name || "-"}</td>
                  <td>{selectedItem.master_item?.sub_category?.sub_category_name || "-"}</td>
                  <td>{selectedItem.master_item?.unit?.unit_name || "-"}</td>
                  <td>{selectedItem.quantity || 0}</td>
                  <td>{selectedItem.reserved_quantity || 0}</td>
                  <td>{selectedItem.available_quantity || 0}</td>
                  <td>{selectedItem.bag_no || "-"}</td>
                  <td>{selectedItem.selling_price || "-"}</td>
                  <td>{selectedItem.selling_price ? (Number(selectedItem.selling_price) * Number(selectedItem.quantity)).toFixed(2) : "-"}</td>
                  <td><TruncatedTextCell text={selectedItem.remark || "-"} /></td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={12} className="text-center py-3">
                    No item selected
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Second table (bag addition) */}
        <div style={{ overflowX: "auto", width: "100%" }}>
          <Table
            hover
            className="align-middle text-center"
            style={{ minWidth: 600 }}
          >
            <thead className="table-light border-bottom">
              <tr>
                <th>Sr No.</th>
                <th>Quantity</th>
                <th style={{ position: "sticky", right: 0, zIndex: 1 }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Add Row */}
              <tr>
                <td></td>
                <td>
                  <TextField
                    size="small"
                    name="quantity"
                    type="text"
                    placeholder="Enter Quantity"
                    fullWidth
                    value={formik.values.quantity}
                     onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        return; // allow control keys
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault(); // block all non-digits
                      }
                    }}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.quantity && Boolean(formik.errors.quantity)
                    }
                    helperText={
                      formik.touched.quantity 
                        ? formik.errors.quantity 
                        : `Available: ${availableQuantity - totalQuantityUsed}`
                    }
                    inputProps={{
                      max: availableQuantity - totalQuantityUsed,
                      min: 0.01,
                      step: 0.01,
                    }}
                  />
                </td>
                <td
                  style={{
                    position: "sticky",
                    right: 0,
                    backgroundColor: "#fff",
                    zIndex: 1,
                  }}
                >
                  <SubmitButton
                    isSubmitting={isAdding}
                    onClick={() => formik.handleSubmit()}
                  />
                </td>
              </tr>

              {/* Existing rows */}
              {rows.map((row, idx) => (
                <tr key={row.id}>
                  <td>{idx + 1}</td>
                  <td>{row.quantity}</td>
                  <td style={{ position: "sticky", right: 0, zIndex: 1 }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(row.id)}
                      disabled={deletingRows.includes(row.id)}
                    >
                      {deletingRows.includes(row.id) ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-center gap-3 py-3">
          <Button
            variant="outline-secondary"
            style={{
              color: "#878899",
              borderColor: "#B0BAC9",
              backgroundColor: "transparent",
            }}
            onClick={handleClose}
          >
            Cancel
          </Button>

          {/* Reusable submit button with spinner */}
          <SubmitButton
            isSubmitting={submitting}
            labels={{
              create: "Submit",
              submitting: "Submitting...",
            }}
            onClick={handleSubmit}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CreateChildBagModal;
